import { EventSourceParser } from "eventsource-parser";

export const isAbortError = (err: unknown): boolean => {
  const name = (err as { name?: string } | null)?.name;
  return name === "AbortError" || name === "CanceledError";
};

/**
 * Process a streaming response from axios with fetch adapter
 * @param response - The axios response with streaming data
 * @param parser - The eventsource parser to process chunks
 */
export async function processStreamResponse(
  response: any,
  parser: EventSourceParser
): Promise<void> {
  const decoder = new TextDecoder("utf-8");

  // Handle streaming response correctly
  // When using axios with fetch adapter, response.data is a ReadableStream
  const reader = response?.data?.getReader();

  if (!reader) {
    throw new Error("Failed to get stream reader");
  }

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) {
        break;
      }

      parser.feed(decoder.decode(value));
    }
  } catch (err) {
    if (isAbortError(err)) {
      return;
    }
    throw err;
  } finally {
    reader.releaseLock();
  }
}

/**
 * Process a streaming response and collect the data
 * @param response - The axios response with streaming data
 * @param onChunk - Callback to process each decoded chunk
 */
export async function processStreamResponseWithCallback(
  response: any,
  onChunk: (chunk: string) => void
): Promise<void> {
  const decoder = new TextDecoder("utf-8");

  // Handle streaming response correctly
  const reader = response?.data?.getReader();

  if (!reader) {
    throw new Error("Failed to get stream reader");
  }

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) {
        break;
      }

      onChunk(decoder.decode(value));
    }
  } finally {
    reader.releaseLock();
  }
}
